// Análisis de paros — la vista de DECISIÓN del coordinador de mantenimiento.
//
// Consume GET /api/mantenimiento/paros/analisis (contrato
// analisisParosMttoQuerySchema / analisisParosMttoResponseSchema):
//   · Barra de filtros sticky: presets de rango (default = semana operativa
//     que resuelve el endpoint), planta → línea encadenados, alcance
//     (solo mantenimiento / todos), programados, estado y granularidad.
//   · KPIs: paros, minutos perdidos, abiertos AHORA (destacado) y — con la
//     capability de costos — unidades no producidas y $ perdido.
//   · Tendencia: barras apiladas por causal madre por bucket (patrón
//     CostoParosChart del directivo: top-3 + "Otras", color de la categoría
//     configurado por el tenant en A3; $ si hay costos, minutos si no).
//   · Causales: donut por madre + drill client-side a las específicas
//     (causal_madre_id), con flag de calidad `es_otro`.
//   · ¿Cuándo?: por turno + perfil horario 0-23 con horas pico resaltadas.
//   · Por línea: tabla ordenable con badge "PARO ACTIVO" — la lista de acción.
//
// La pestaña "Paros" de Informes queda como está: esta pantalla es la vista
// rica; la limpieza de aquella es una decisión posterior.

import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AnalisisParoCausalEspecificaMtto,
  AnalisisParoLineaMtto,
  AnalisisParosMttoResponse,
  EstadoParoFiltro,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { Input } from '../../components/ui/input';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { useEstructuraMtto } from './estructura';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import { useAnalisisParosMtto, type AnalisisParosMttoUiQuery } from './queries';
import { fmtNum, hoyIso } from './shared';

// ---------------------------------------------------------------------------
// Formatos (es-CO, mismos criterios que el directivo y C8)
// ---------------------------------------------------------------------------

/** Pesos compactos: ≥1M → "$1,2 M" (formato del informe ejecutivo del directivo). */
function copM(n: number): string {
  if (Math.abs(n) >= 1_000_000)
    return `$${(n / 1_000_000).toLocaleString('es-CO', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} M`;
  return `$${new Intl.NumberFormat('es-CO').format(Math.round(n))}`;
}

/** Minutos legibles: "45 min" / "3 h 20 m". */
function fmtMin(minutos: number): string {
  const m = Math.round(minutos);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const resto = m % 60;
  return resto === 0 ? `${fmtNum(h)} h` : `${fmtNum(h)} h ${resto} m`;
}

/** Horas con un decimal para ejes de charts. */
function horasEje(minutos: number): string {
  return `${(minutos / 60).toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`;
}

/** 'YYYY-MM-DD' → "5 jul" (sin shift de zona horaria). */
function fmtFechaCorta(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\./g, '')
    .trim();
}

/** Label del bucket de tendencia: fecha corta o "S27" para 'IYYY-Wxx'. */
function labelBucket(bucket: string, granularidad: 'dia' | 'semana'): string {
  if (granularidad === 'semana') {
    const w = bucket.split('-W')[1];
    return w ? `S${w}` : bucket;
  }
  return fmtFechaCorta(bucket);
}

const TOOLTIP_STYLE = {
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--color-fg)',
} as const;

// Fallback cuando la categoría no tiene color configurado (A3).
const FALLBACK_FILLS = [
  'var(--color-crit)',
  'var(--color-warn)',
  'var(--color-info)',
  'var(--color-accent)',
  'var(--color-ok)',
  'var(--color-fg-tertiary)',
];

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

type Preset = 'semana' | 'ultimas4' | 'mes' | 'personalizado';

const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: 'semana', label: 'Esta semana' },
  { value: 'ultimas4', label: 'Últimas 4 semanas' },
  { value: 'mes', label: 'Este mes' },
  { value: 'personalizado', label: 'Personalizado' },
];

interface FiltrosState {
  preset: Preset;
  /** Solo aplican con preset 'personalizado'. */
  desde?: string;
  hasta?: string;
  plantaId?: string;
  lineaId?: string;
  soloMantenimiento: boolean;
  incluirProgramados: boolean;
  estado: EstadoParoFiltro;
  granularidad: 'dia' | 'semana';
}

const FILTROS_INICIALES: FiltrosState = {
  preset: 'semana',
  soloMantenimiento: true,
  incluirProgramados: false,
  estado: 'todos',
  granularidad: 'dia',
};

/** Rango efectivo del preset. 'semana' no envía fechas: decide el endpoint. */
function rangoDePreset(f: FiltrosState): { desde?: string; hasta?: string } {
  const hoy = hoyIso();
  if (f.preset === 'ultimas4') {
    const d = new Date(`${hoy}T00:00:00`);
    d.setDate(d.getDate() - 27);
    const desde = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { desde, hasta: hoy };
  }
  if (f.preset === 'mes') return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
  if (f.preset === 'personalizado') return { desde: f.desde, hasta: f.hasta };
  return {};
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function PantallaAnalisisParosMtto() {
  const [filtros, setFiltros] = useState<FiltrosState>(FILTROS_INICIALES);

  const query: AnalisisParosMttoUiQuery = useMemo(() => {
    const rango = rangoDePreset(filtros);
    // Rango incompleto o invertido en personalizado → no mandar fechas a
    // medias (el endpoint cae a su default y la UI no rompe).
    const rangoValido = rango.desde && rango.hasta && rango.desde <= rango.hasta;
    return {
      ...(rangoValido ? { desde: rango.desde, hasta: rango.hasta } : {}),
      planta_id: filtros.plantaId,
      linea_id: filtros.lineaId,
      solo_mantenimiento: filtros.soloMantenimiento,
      incluir_programados: filtros.incluirProgramados,
      estado_paro: filtros.estado,
      granularidad: filtros.granularidad,
    };
  }, [filtros]);

  const { data, isLoading, isError, error, isFetching } = useAnalisisParosMtto(query);

  const [madreSel, setMadreSel] = useState<string | null>(null);
  // Si el filtro cambió y la madre seleccionada ya no existe, se ignora.
  const madreActiva =
    madreSel && data?.por_causal_madre.some((m) => m.causal_madre_id === madreSel)
      ? madreSel
      : null;

  return (
    <MantenimientoShell
      titulo="Análisis de paros"
      subtitulo="Dónde, cuándo y por qué se detiene la producción — priorización del mantenimiento"
    >
      <div className="flex flex-col gap-4">
        <FiltrosBar
          filtros={filtros}
          onChange={setFiltros}
          rango={data?.rango ?? null}
          actualizando={isFetching && !isLoading}
        />

        {isLoading ? <CargandoModulo mensaje="Cargando análisis de paros…" /> : null}
        {isError ? <ErrorModulo error={error} /> : null}

        {data ? (
          <>
            {filtros.soloMantenimiento && !data.filtro_mantenimiento_aplicado ? (
              <Card variant="warn-left">
                <CardContent className="flex items-start gap-3 p-4 pt-4">
                  <span className="mt-0.5 text-warn">
                    <Icon name="alert" size={18} stroke={1.6} />
                  </span>
                  <p className="text-xs text-fg-secondary">
                    El catálogo de causales del tenant no tiene la categoría de mantenimiento, por
                    lo que se muestran <strong className="text-fg">todos los paros</strong> del
                    rango. Solicite al administrador configurar la categoría en la pantalla de
                    causales.
                  </p>
                </CardContent>
              </Card>
            ) : null}

            <KpisRow data={data} />
            <TendenciaCard data={data} />

            <section className="grid grid-cols-1 gap-4 lg:grid-cols-5">
              <CausalesMadreCard data={data} madreSel={madreActiva} onSelect={setMadreSel} />
              <EspecificasCard
                data={data}
                madreSel={madreActiva}
                onLimpiar={() => setMadreSel(null)}
              />
            </section>

            <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <TurnoCard data={data} />
              <PerfilHorarioCard data={data} />
            </section>

            <LineasCard data={data} />
          </>
        ) : null}
      </div>
    </MantenimientoShell>
  );
}

// ---------------------------------------------------------------------------
// Barra de filtros (sticky)
// ---------------------------------------------------------------------------

function FiltrosBar({
  filtros,
  onChange,
  rango,
  actualizando,
}: {
  filtros: FiltrosState;
  onChange: (f: FiltrosState) => void;
  /** Rango que respondió el endpoint (para mostrar las fechas efectivas). */
  rango: { desde: string; hasta: string } | null;
  actualizando: boolean;
}) {
  const estructura = useEstructuraMtto();
  const update = (patch: Partial<FiltrosState>) => onChange({ ...filtros, ...patch });

  // Fechas visibles: las del estado en personalizado; si no, las efectivas
  // que devolvió el endpoint. Editar una fecha pasa a 'personalizado'
  // sembrando el rango actual para que el otro extremo no quede vacío.
  const desdeVisible =
    filtros.preset === 'personalizado' ? (filtros.desde ?? '') : (rango?.desde ?? '');
  const hastaVisible =
    filtros.preset === 'personalizado' ? (filtros.hasta ?? '') : (rango?.hasta ?? '');
  const editarFecha = (campo: 'desde' | 'hasta', valor: string) =>
    onChange({
      ...filtros,
      preset: 'personalizado',
      desde: campo === 'desde' ? valor : desdeVisible || undefined,
      hasta: campo === 'hasta' ? valor : hastaVisible || undefined,
    });

  const lineasDePlanta = filtros.plantaId
    ? estructura.lineas.filter((l) => l.planta_id === filtros.plantaId)
    : estructura.lineas;

  return (
    <section className="sticky top-0 z-20 rounded-md border border-border bg-surface-base/95 p-3 shadow-subtle backdrop-blur">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <FiltroBloque label="Período">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl<Preset>
              value={filtros.preset}
              onChange={(preset) =>
                onChange({
                  ...filtros,
                  preset,
                  // Sembrar personalizado con el rango efectivo actual.
                  desde: preset === 'personalizado' ? desdeVisible || undefined : undefined,
                  hasta: preset === 'personalizado' ? hastaVisible || undefined : undefined,
                })
              }
              size="sm"
              options={PRESETS}
            />
            <div className="flex items-center gap-1.5">
              <Input
                id="paros-desde"
                aria-label="Desde"
                type="date"
                className="h-7 w-[132px] text-xs"
                value={desdeVisible}
                onChange={(e) => editarFecha('desde', e.target.value)}
              />
              <span className="text-xs text-fg-tertiary">→</span>
              <Input
                id="paros-hasta"
                aria-label="Hasta"
                type="date"
                className="h-7 w-[132px] text-xs"
                value={hastaVisible}
                onChange={(e) => editarFecha('hasta', e.target.value)}
              />
            </div>
          </div>
        </FiltroBloque>

        <FiltroBloque label="Planta">
          <Select
            id="paros-planta"
            className="h-7 min-w-[140px] py-0 text-xs"
            value={filtros.plantaId ?? ''}
            disabled={!estructura.disponible}
            onChange={(e) => update({ plantaId: e.target.value || undefined, lineaId: undefined })}
          >
            <option value="">Todas</option>
            {estructura.plantas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </Select>
        </FiltroBloque>

        <FiltroBloque label="Línea">
          <Select
            id="paros-linea"
            className="h-7 min-w-[150px] py-0 text-xs"
            value={filtros.lineaId ?? ''}
            disabled={!estructura.disponible}
            onChange={(e) => update({ lineaId: e.target.value || undefined })}
          >
            <option value="">Todas</option>
            {lineasDePlanta.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nombre}
              </option>
            ))}
          </Select>
        </FiltroBloque>

        <FiltroBloque label="Alcance">
          <SegmentedControl<'mtto' | 'todos'>
            value={filtros.soloMantenimiento ? 'mtto' : 'todos'}
            onChange={(v) => update({ soloMantenimiento: v === 'mtto' })}
            size="sm"
            options={[
              { value: 'mtto', label: 'Solo mantenimiento' },
              { value: 'todos', label: 'Todos los paros' },
            ]}
          />
        </FiltroBloque>

        <FiltroBloque label="Estado">
          <SegmentedControl<EstadoParoFiltro>
            value={filtros.estado}
            onChange={(estado) => update({ estado })}
            size="sm"
            options={[
              { value: 'todos', label: 'Todos' },
              { value: 'abiertos', label: 'Abiertos' },
              { value: 'cerrados', label: 'Cerrados' },
            ]}
          />
        </FiltroBloque>

        <FiltroBloque label="Tendencia">
          <SegmentedControl<'dia' | 'semana'>
            value={filtros.granularidad}
            onChange={(granularidad) => update({ granularidad })}
            size="sm"
            options={[
              { value: 'dia', label: 'Día' },
              { value: 'semana', label: 'Semana' },
            ]}
          />
        </FiltroBloque>

        <div className="flex h-7 items-center gap-3">
          <label className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-fg-secondary">
            <input
              type="checkbox"
              className="size-3.5"
              style={{ accentColor: 'var(--color-accent)' }}
              checked={filtros.incluirProgramados}
              onChange={(e) => update({ incluirProgramados: e.target.checked })}
            />
            Incluir programados
          </label>
          <Button variant="outline" size="sm" onClick={() => onChange(FILTROS_INICIALES)}>
            Restablecer
          </Button>
          {actualizando ? (
            <span className="text-[10px] text-fg-tertiary">Actualizando…</span>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function FiltroBloque({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </Label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------

function KpisRow({ data }: { data: AnalisisParosMttoResponse }) {
  const { totales, con_costos } = data;
  const abiertos = totales.paros_abiertos;

  return (
    <Card>
      <CardContent
        className={cn(
          'grid grid-cols-2 gap-4 p-5 pt-5',
          con_costos ? 'md:grid-cols-3 xl:grid-cols-5' : 'md:grid-cols-3',
        )}
      >
        <KpiCard
          label="Paros"
          value={fmtNum(totales.paros_count)}
          sub={`${fmtFechaCorta(data.rango.desde)} → ${fmtFechaCorta(data.rango.hasta)}`}
        />
        <KpiCard
          label="Minutos perdidos"
          value={fmtNum(totales.minutos_totales)}
          sub={`≈ ${horasEje(totales.minutos_totales)} · solo paros cerrados`}
          divider
        />
        <KpiCard
          label="Abiertos ahora"
          value={
            abiertos > 0 ? <span className="text-crit">{fmtNum(abiertos)}</span> : fmtNum(abiertos)
          }
          sub={
            abiertos > 0
              ? `${fmtMin(totales.minutos_paros_abiertos)} transcurridos — requieren atención`
              : 'Sin paros abiertos en el filtro'
          }
          subTone={abiertos > 0 ? 'crit' : 'ok'}
          divider
        />
        {con_costos ? (
          <>
            <KpiCard
              label="Unidades no producidas"
              value={fmtNum(totales.unidades_no_producidas ?? 0)}
              sub="tiempo de paro × capacidad del SKU"
              divider
            />
            <KpiCard
              label="$ Perdido"
              value={copM(totales.valor_cop ?? 0)}
              sub={
                totales.skus_sin_precio > 0
                  ? `valor parcial: ${fmtNum(totales.skus_sin_precio)} SKU(s) sin precio`
                  : 'valor de venta no producido'
              }
              subTone={totales.skus_sin_precio > 0 ? 'warn' : 'neutral'}
              divider
            />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tendencia — barras apiladas por causal madre (patrón CostoParosChart)
// ---------------------------------------------------------------------------

const OTRAS = 'Otras';

function TendenciaCard({ data }: { data: AnalisisParosMttoResponse }) {
  const conCostos = data.con_costos;

  const { chartData, series } = useMemo(() => {
    // Top-3 causales madre del período por peso ($ u horas) + "Otras".
    const porCausal = new Map<string, number>();
    const colorDe = new Map<string, string>();
    for (const p of data.tendencia) {
      const peso = conCostos ? (p.valor_cop ?? 0) : p.minutos;
      porCausal.set(p.nombre, (porCausal.get(p.nombre) ?? 0) + peso);
      if (p.color && !colorDe.has(p.nombre)) colorDe.set(p.nombre, p.color);
    }
    const top = [...porCausal.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([c]) => c);
    const nombres = porCausal.size > top.length ? [...top, OTRAS] : top;
    const series = nombres.map((nombre, i) => ({
      nombre,
      fill: colorDe.get(nombre) ?? FALLBACK_FILLS[i % FALLBACK_FILLS.length]!,
    }));

    const porBucket = new Map<string, Record<string, number | string>>();
    for (const p of data.tendencia) {
      const serie = top.includes(p.nombre) ? p.nombre : OTRAS;
      const fila = porBucket.get(p.bucket) ?? {
        bucket: p.bucket,
        label: labelBucket(p.bucket, data.granularidad),
        total: 0,
      };
      const valor = conCostos ? (p.valor_cop ?? 0) : p.minutos;
      fila[serie] = (Number(fila[serie]) || 0) + valor;
      fila.total = (Number(fila.total) || 0) + valor;
      porBucket.set(p.bucket, fila);
    }
    const chartData = [...porBucket.values()].sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket)),
    );
    return { chartData, series };
  }, [data.tendencia, data.granularidad, conCostos]);

  const fmtValor = (v: number) => (conCostos ? copM(v) : horasEje(v));

  return (
    <Card>
      <CardContent className="p-4 pt-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">
              {conCostos ? 'Costo de los paros' : 'Tiempo de paro'} por{' '}
              {data.granularidad === 'dia' ? 'día' : 'semana'}
            </h2>
            <p className="text-[11px] text-fg-tertiary">
              Apilado por causal madre — top 3 del período + Otras
              {conCostos ? ' · valor de venta no producido' : ''}
            </p>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-sm text-fg-tertiary">
            Sin paros con duración en el período seleccionado.
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 0, left: -8, bottom: 0 }}
                barCategoryGap="24%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => fmtValor(Number(v))}
                  tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-surface-overlay)' }}
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v, name) => [fmtValor(Number(v)), String(name)]}
                />
                {series.map((s, i) => (
                  <Bar
                    key={s.nombre}
                    dataKey={s.nombre}
                    stackId="paros"
                    name={s.nombre}
                    fill={s.fill}
                    maxBarSize={44}
                    radius={i === series.length - 1 ? [2, 2, 0, 0] : undefined}
                  />
                ))}
                {/* Total del stack: línea invisible cuyo Y es el total exacto */}
                <Line
                  dataKey="total"
                  stroke="none"
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  tooltipType="none"
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey="total"
                    position="top"
                    offset={8}
                    formatter={(v: unknown) => (Number(v) > 0 ? fmtValor(Number(v)) : '')}
                    style={{ fontSize: 11, fill: 'var(--color-fg)', fontWeight: 700 }}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {series.map((s) => (
                <span
                  key={s.nombre}
                  className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary"
                >
                  <span
                    className="inline-block size-2.5 rounded-[2px]"
                    style={{ background: s.fill }}
                  />
                  {s.nombre}
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Causales — donut por madre + drill client-side a específicas
// ---------------------------------------------------------------------------

function CausalesMadreCard({
  data,
  madreSel,
  onSelect,
}: {
  data: AnalisisParosMttoResponse;
  madreSel: string | null;
  onSelect: (id: string | null) => void;
}) {
  const madres = useMemo(
    () =>
      data.por_causal_madre.map((m, i) => ({
        ...m,
        fill: m.categoria.color ?? FALLBACK_FILLS[i % FALLBACK_FILLS.length]!,
      })),
    [data.por_causal_madre],
  );
  const conDatos = madres.filter((m) => m.minutos > 0);

  const toggle = (id: string) => onSelect(madreSel === id ? null : id);

  return (
    <Card className="lg:col-span-2">
      <CardContent className="p-4 pt-4">
        <h2 className="text-sm font-medium text-fg">Causales madre</h2>
        <p className="text-[11px] text-fg-tertiary">
          Participación en minutos perdidos — seleccione una para ver sus específicas
        </p>

        {conDatos.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">
            Sin minutos de paro en el rango seleccionado.
          </p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={conDatos.map((m) => ({
                    name: m.nombre,
                    value: Math.round(m.minutos),
                    pct: m.pct_minutos,
                    id: m.causal_madre_id,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  onClick={(sector) => {
                    // Recharts tipa el sector como PieSectorDataItem; el datum
                    // original (con nuestro id) viaja mezclado y en payload.
                    const s = sector as { id?: string; payload?: { id?: string } };
                    const id = s.id ?? s.payload?.id;
                    if (id) toggle(id);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {conDatos.map((m) => (
                    <Cell
                      key={m.causal_madre_id}
                      fill={m.fill}
                      fillOpacity={madreSel && madreSel !== m.causal_madre_id ? 0.35 : 1}
                      stroke="var(--color-surface-base)"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={
                    ((value: unknown, name: unknown, props: { payload?: { pct?: number } }) => [
                      `${fmtMin(Number(value ?? 0))} (${props.payload?.pct?.toFixed(1) ?? 0}%)`,
                      String(name ?? ''),
                    ]) as never
                  }
                />
              </PieChart>
            </ResponsiveContainer>

            <ul className="mt-2 flex flex-col gap-0.5">
              {madres.map((m) => {
                const activa = madreSel === m.causal_madre_id;
                return (
                  <li key={m.causal_madre_id}>
                    <button
                      type="button"
                      onClick={() => toggle(m.causal_madre_id)}
                      className={cn(
                        'flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[11px] transition-colors hover:bg-surface-overlay/60',
                        activa && 'bg-surface-overlay ring-1 ring-accent',
                      )}
                    >
                      <span
                        aria-hidden
                        className="inline-block size-2 flex-shrink-0 rounded-sm"
                        style={{ backgroundColor: m.fill }}
                      />
                      <span className="truncate text-fg-secondary" title={m.nombre}>
                        {m.nombre}
                      </span>
                      <span className="ml-auto shrink-0 tabular-nums text-fg-tertiary">
                        {fmtMin(m.minutos)} · {m.pct_minutos.toFixed(1)}%
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EspecificasCard({
  data,
  madreSel,
  onLimpiar,
}: {
  data: AnalisisParosMttoResponse;
  madreSel: string | null;
  onLimpiar: () => void;
}) {
  const conCostos = data.con_costos;
  const madreNombre = madreSel
    ? (data.por_causal_madre.find((m) => m.causal_madre_id === madreSel)?.nombre ?? null)
    : null;

  const filas: AnalisisParoCausalEspecificaMtto[] = useMemo(() => {
    const base = madreSel
      ? data.por_causal_especifica.filter((c) => c.causal_madre_id === madreSel)
      : data.por_causal_especifica;
    return [...base].sort((a, b) => b.minutos - a.minutos);
  }, [data.por_causal_especifica, madreSel]);

  const hayOtro = filas.some((c) => c.es_otro);

  return (
    <Card className="lg:col-span-3">
      <CardContent className="flex h-full flex-col gap-3 p-4 pt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">
              Causales específicas
              {madreNombre ? <span className="text-fg-tertiary"> · {madreNombre}</span> : null}
            </h2>
            <p className="text-[11px] text-fg-tertiary">
              {fmtNum(filas.length)} causal(es) · ordenadas por minutos perdidos
            </p>
          </div>
          {madreSel ? (
            <Button variant="outline" size="sm" onClick={onLimpiar}>
              Ver todas
            </Button>
          ) : null}
        </div>

        {filas.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">
            Sin causales específicas en el rango seleccionado.
          </p>
        ) : (
          <Table maxHeight="380px">
            <THead sticky>
              <TR>
                <TH>Causal específica</TH>
                {madreSel ? null : <TH>Causal madre</TH>}
                <TH align="right">Paros</TH>
                <TH align="right">Minutos</TH>
                {conCostos ? <TH align="right">$ Perdido</TH> : null}
                <TH align="right">% minutos</TH>
              </TR>
            </THead>
            <TBody>
              {filas.map((c) => (
                <TR key={c.causal_id}>
                  <TD>
                    <span className="flex items-center gap-2">
                      {c.nombre}
                      {c.es_otro ? (
                        <Badge
                          tone="warn"
                          className="text-[9px]"
                          title="Causal genérica — revise la clasificación"
                        >
                          Otro
                        </Badge>
                      ) : null}
                    </span>
                  </TD>
                  {madreSel ? null : (
                    <TD className="text-xs text-fg-tertiary">
                      {data.por_causal_madre.find((m) => m.causal_madre_id === c.causal_madre_id)
                        ?.nombre ?? '—'}
                    </TD>
                  )}
                  <TD align="right" numeric className="text-fg-secondary">
                    {fmtNum(c.paros_count)}
                  </TD>
                  <TD align="right" numeric>
                    {fmtNum(c.minutos)}
                  </TD>
                  {conCostos ? (
                    <TD align="right" numeric className="text-fg-secondary">
                      {c.valor_cop !== undefined && c.valor_cop > 0 ? copM(c.valor_cop) : '—'}
                    </TD>
                  ) : null}
                  <TD align="right" numeric className="text-xs text-fg-tertiary">
                    {c.pct_minutos.toFixed(1)}%
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        {hayOtro ? (
          <p className="text-[10.5px] text-fg-tertiary">
            Las causales marcadas como <strong className="text-warn">Otro</strong> son genéricas —
            revise la clasificación con los líderes o agregue causales faltantes a la jerarquía.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ¿Cuándo ocurren? — por turno + perfil horario
// ---------------------------------------------------------------------------

/** Tooltip común: label · minutos · paros. */
function ParosBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: { label: string; minutos: number; paros_count: number } }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-xs text-fg shadow-sm">
      <p className="font-medium">{d.label}</p>
      <p className="mt-0.5 tabular-nums text-fg-secondary">
        {fmtMin(d.minutos)} · {fmtNum(d.paros_count)} paro(s)
      </p>
    </div>
  );
}

function TurnoCard({ data }: { data: AnalisisParosMttoResponse }) {
  const turnos = useMemo(
    () =>
      [...data.por_turno]
        .sort((a, b) => a.turno_codigo.localeCompare(b.turno_codigo))
        .map((t) => ({
          label: t.turno_codigo,
          minutos: Math.round(t.minutos),
          paros_count: t.paros_count,
        })),
    [data.por_turno],
  );

  return (
    <Card>
      <CardContent className="p-4 pt-4">
        <h2 className="text-sm font-medium text-fg">Por turno</h2>
        <p className="mb-3 text-[11px] text-fg-tertiary">Minutos perdidos por turno operativo</p>
        {turnos.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
        ) : (
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={turnos} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                content={<ParosBarTooltip />}
              />
              <Bar
                dataKey="minutos"
                fill="var(--color-warn)"
                fillOpacity={0.85}
                radius={[3, 3, 0, 0]}
                maxBarSize={64}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function PerfilHorarioCard({ data }: { data: AnalisisParosMttoResponse }) {
  const { horas, maxMin } = useMemo(() => {
    // Rellenar las 24 horas: el perfil se lee como reloj de pared completo.
    const porHora = new Map(data.perfil_horario.map((h) => [h.hora, h]));
    const horas = Array.from({ length: 24 }, (_, hora) => {
      const h = porHora.get(hora);
      return {
        label: `${String(hora).padStart(2, '0')}h`,
        minutos: Math.round(h?.minutos ?? 0),
        paros_count: h?.paros_count ?? 0,
      };
    });
    const maxMin = Math.max(...horas.map((h) => h.minutos));
    return { horas, maxMin };
  }, [data.perfil_horario]);

  // Hora pico = ≥80% del máximo (resaltada en crítico).
  const esPico = (minutos: number) => maxMin > 0 && minutos >= maxMin * 0.8;

  return (
    <Card>
      <CardContent className="p-4 pt-4">
        <h2 className="text-sm font-medium text-fg">Perfil horario</h2>
        <p className="mb-3 text-[11px] text-fg-tertiary">
          Minutos perdidos por hora de inicio del paro ·{' '}
          <span className="text-crit">barras resaltadas = horas pico</span>
        </p>
        {maxMin === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
        ) : (
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={horas} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 9.5, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                interval={2}
              />
              <YAxis
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                content={<ParosBarTooltip />}
              />
              <Bar dataKey="minutos" radius={[2, 2, 0, 0]} maxBarSize={20}>
                {horas.map((h) => (
                  <Cell
                    key={h.label}
                    fill={esPico(h.minutos) ? 'var(--color-crit)' : 'var(--color-info)'}
                    fillOpacity={esPico(h.minutos) ? 0.95 : 0.55}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Por línea — la lista de acción del coordinador
// ---------------------------------------------------------------------------

type ColLinea = 'linea' | 'paros' | 'minutos' | 'valor' | 'estado';
type SortLineas = { col: ColLinea; dir: 'asc' | 'desc' };

function SortTh({
  label,
  col,
  sort,
  onSort,
  align = 'right',
}: {
  label: string;
  col: ColLinea;
  sort: SortLineas;
  onSort: (col: ColLinea) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.col === col;
  return (
    <TH align={align}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wider hover:text-fg',
          align === 'right' && 'flex-row-reverse',
          active && 'text-fg',
        )}
      >
        <span
          aria-hidden
          className={cn('text-[8px]', active ? 'text-accent' : 'text-fg-tertiary/40')}
        >
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
        {label}
      </button>
    </TH>
  );
}

function LineasCard({ data }: { data: AnalisisParosMttoResponse }) {
  const conCostos = data.con_costos;
  const [sort, setSort] = useState<SortLineas>({ col: 'minutos', dir: 'desc' });
  const onSort = (col: ColLinea) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' },
    );

  const lineas = useMemo(() => {
    const f = sort.dir === 'asc' ? 1 : -1;
    const cmp = (a: AnalisisParoLineaMtto, b: AnalisisParoLineaMtto): number => {
      switch (sort.col) {
        case 'linea':
          return a.nombre.localeCompare(b.nombre);
        case 'paros':
          return a.paros_count - b.paros_count;
        case 'valor':
          return (a.valor_cop ?? 0) - (b.valor_cop ?? 0);
        case 'estado':
          return Number(a.paro_activo) - Number(b.paro_activo);
        default:
          return a.minutos - b.minutos;
      }
    };
    return [...data.por_linea].sort((a, b) => f * cmp(a, b));
  }, [data.por_linea, sort]);

  const activas = data.por_linea.filter((l) => l.paro_activo).length;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 pt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-fg">Por línea</h2>
            <p className="text-[11px] text-fg-tertiary">
              {fmtNum(lineas.length)} línea(s) con paros · clic en una columna para ordenar
            </p>
          </div>
          {activas > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-crit">
              <span
                aria-hidden
                className="inline-block size-1.5 animate-pulse rounded-full bg-crit"
              />
              {fmtNum(activas)} línea(s) con paro activo
            </span>
          ) : null}
        </div>

        {lineas.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">
            Sin líneas con paros en el rango seleccionado.
          </p>
        ) : (
          <Table maxHeight="60vh">
            <THead sticky>
              <TR>
                <SortTh label="Línea" col="linea" sort={sort} onSort={onSort} align="left" />
                <SortTh label="Paros" col="paros" sort={sort} onSort={onSort} />
                <SortTh label="Minutos" col="minutos" sort={sort} onSort={onSort} />
                {conCostos ? (
                  <SortTh label="$ Perdido" col="valor" sort={sort} onSort={onSort} />
                ) : null}
                <SortTh label="Estado" col="estado" sort={sort} onSort={onSort} />
              </TR>
            </THead>
            <TBody>
              {lineas.map((l) => (
                <TR key={l.linea_id} className={cn(l.paro_activo && 'bg-crit-soft/30')}>
                  <TD>
                    <span className="font-medium text-fg">{l.nombre}</span>
                    <span className="block text-[10.5px] text-fg-tertiary">{l.planta_nombre}</span>
                  </TD>
                  <TD align="right" numeric className="text-fg-secondary">
                    {fmtNum(l.paros_count)}
                  </TD>
                  <TD
                    align="right"
                    numeric
                    className={cn(l.minutos >= 480 ? 'font-medium text-crit' : undefined)}
                  >
                    {fmtNum(l.minutos)}
                  </TD>
                  {conCostos ? (
                    <TD align="right" numeric className="text-fg-secondary">
                      {l.valor_cop !== undefined && l.valor_cop > 0 ? copM(l.valor_cop) : '—'}
                    </TD>
                  ) : null}
                  <TD align="right">
                    {l.paro_activo ? (
                      <Badge tone="crit" dot>
                        Paro activo
                      </Badge>
                    ) : (
                      <span className="text-xs text-fg-tertiary">—</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
